
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Mail, User, Calendar, Folder, FileText } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';

interface ViewProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  project: any;
}

const ViewProjectDialog: React.FC<ViewProjectDialogProps> = ({
  isOpen,
  onClose,
  project
}) => {
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && project) {
      fetchProjectFeedbacks();
    }
  }, [isOpen, project]);

  const fetchProjectFeedbacks = async () => {
    setLoading(true);
    try {
      // Get all feedbacks associated with this project
      const { data: feedbackRelations, error: relationsError } = await supabase
        .from('projects_feedbacks')
        .select('feedback_id')
        .eq('project_id', project.id);
      
      if (relationsError) throw relationsError;
      
      const feedbackIds = feedbackRelations?.map(relation => relation.feedback_id) || [];
      
      if (feedbackIds.length === 0) {
        setFeedbacks([]);
        return;
      }
      
      // Get the actual feedback data
      const { data: feedbacksData, error: feedbacksError } = await supabase
        .from('feedbacks')
        .select('*')
        .in('id', feedbackIds);
      
      if (feedbacksError) throw feedbacksError;
      
      setFeedbacks(feedbacksData || []);
    } catch (error) {
      console.error('Error fetching project feedbacks:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les feedbacks du projet.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  if (!project) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Folder className="h-5 w-5 mr-2 text-blue-600" />
            Détails du projet
          </DialogTitle>
          <DialogDescription>
            {project.name}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="details">Informations</TabsTrigger>
            <TabsTrigger value="feedbacks">Feedbacks ({project.feedbackCount || 0})</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-lg">Informations générales</h3>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Folder className="h-4 w-4 mr-2 text-gray-500" />
                      <span className="font-medium mr-2">Nom :</span> 
                      {project.name}
                    </div>
                    <div className="flex items-center text-sm">
                      <FileText className="h-4 w-4 mr-2 text-gray-500" />
                      <span className="font-medium mr-2">Catégorie :</span> 
                      {project.category || 'Non spécifiée'}
                    </div>
                    <div className="flex items-center text-sm">
                      <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                      <span className="font-medium mr-2">Créé le :</span> 
                      {new Date(project.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-lg">Propriétaire</h3>
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-blue-100 text-blue-600">
                        {getInitials(project.profiles?.full_name || 'Utilisateur')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{project.profiles?.full_name || 'Non spécifié'}</p>
                      <div className="flex items-center text-sm text-gray-500">
                        <Mail className="h-3 w-3 mr-1" />
                        {project.profiles?.email || 'Email non disponible'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-lg mb-2">Description</h3>
                <div className="p-4 bg-gray-50 rounded-md whitespace-pre-wrap">
                  {project.description || 'Aucune description fournie.'}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-md flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Feedbacks demandés</p>
                    <p className="text-xl font-bold">{project.feedbackCount || 0}</p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-500 opacity-70" />
                </div>
                
                <div className="bg-green-50 p-4 rounded-md flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Réponses reçues</p>
                    <p className="text-xl font-bold">{project.responseCount || 0}</p>
                  </div>
                  <Badge className="h-8 px-3 py-1 text-sm bg-green-500">
                    {project.feedbackCount ? Math.round((project.responseCount / project.feedbackCount) * 100) : 0}%
                  </Badge>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="feedbacks">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Candidat</TableHead>
                      <TableHead>Recruteur</TableHead>
                      <TableHead>Poste</TableHead>
                      <TableHead>Créé le</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feedbacks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                          Aucun feedback trouvé pour ce projet
                        </TableCell>
                      </TableRow>
                    ) : (
                      feedbacks.map(feedback => (
                        <TableRow key={feedback.id}>
                          <TableCell className="font-mono text-xs">{feedback.id.substring(0, 8)}...</TableCell>
                          <TableCell>{feedback.candidate_name || feedback.candidate_email || 'N/A'}</TableCell>
                          <TableCell>{feedback.recruiter_name || feedback.recruiter_email || 'N/A'}</TableCell>
                          <TableCell>{feedback.job_title || 'N/A'}</TableCell>
                          <TableCell>{new Date(feedback.created_at).toLocaleDateString('fr-FR')}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {project.feedbackCount > 0 && feedbacks.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-lg mb-2">Synthèse des feedbacks</h3>
                <div className="p-4 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-500 mb-2">
                    La synthèse générée par IA afficherait ici une analyse des points communs, 
                    tendances et recommandations basées sur tous les feedbacks du projet.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-2"
                    onClick={() => toast({
                      title: "Fonctionnalité à venir",
                      description: "La génération de synthèse IA sera disponible prochainement."
                    })}
                  >
                    Générer une synthèse IA
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ViewProjectDialog;
